import {
  AdminModulePermissions,
  ADMIN_MODULE_VALUES,
  AdminRole,
  isAccessLevel,
  isAdminRole,
} from "../roles";

/**
 * Decode the (base64url) payload of a JWT WITHOUT verifying its signature.
 * This is for read-only UI hints only — never a trust decision. The backend
 * verifies the token on every request. Returns null on any malformed input.
 */
export const decodeJwtPayload = (
  token: string | null | undefined,
): Record<string, unknown> | null => {
  if (!token) return null;
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    );
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
};

/**
 * Whether the access token carries the base `admin` custom claim. This is the
 * same claim the backend's requireAdmin gate enforces — a read-only UI hint used
 * to keep non-admins out of the admin shell, never a trust decision (the backend
 * re-verifies on every request). Returns false for any malformed/absent token.
 */
export const isAdminFromToken = (token: string | null | undefined): boolean => {
  return decodeJwtPayload(token)?.admin === true;
};

/** Extract the `adminRole` claim from an access token, or null if absent. */
export const getAdminRoleFromToken = (
  token: string | null | undefined,
): AdminRole | null => {
  const payload = decodeJwtPayload(token);
  const role = payload?.adminRole;
  return isAdminRole(role) ? role : null;
};

/**
 * Extract the manager's per-module grant map from the `adminModules` claim.
 * Unknown module keys and invalid levels are dropped (fail closed), same as
 * the backend's `parseModulePermissions`. Returns null when the claim is absent
 * — i.e. for every tier role.
 */
export const getAdminModulesFromToken = (
  token: string | null | undefined,
): AdminModulePermissions | null => {
  const claim = decodeJwtPayload(token)?.adminModules;
  if (!claim || typeof claim !== "object") return null;
  const source = claim as Record<string, unknown>;
  const parsed: AdminModulePermissions = {};
  ADMIN_MODULE_VALUES.forEach((module) => {
    const level = source[module];
    if (isAccessLevel(level)) parsed[module] = level;
  });
  return parsed;
};

export interface TokenUserIdentity {
  uid: string | null;
  email: string | null;
  name: string | null;
}

/**
 * Read the signed-in user's identity straight from the verified access token's
 * claims (Firebase ID token: `user_id`/`sub`, `email`, `name`). Read-only UI
 * hint — never a trust decision. Survives reloads because the token is persisted
 * in Redux, unlike the in-memory React Query `USER` cache.
 */
export const getUserIdentityFromToken = (
  token: string | null | undefined,
): TokenUserIdentity => {
  const payload = decodeJwtPayload(token);
  if (!payload) return { uid: null, email: null, name: null };
  const stringClaim = (value: unknown): string | null =>
    typeof value === "string" && value.length > 0 ? value : null;
  return {
    uid: stringClaim(payload.user_id) ?? stringClaim(payload.sub),
    email: stringClaim(payload.email),
    name: stringClaim(payload.name),
  };
};

/**
 * The signed-in uid from the access token (Firebase ID tokens carry it as
 * `user_id` and `sub`). A read-only UI hint — e.g. "assign to me" — never a
 * trust decision: the backend derives the actor from the verified token.
 */
export const getUidFromToken = (
  token: string | null | undefined,
): string | undefined => {
  const payload = decodeJwtPayload(token);
  const uid = payload?.user_id ?? payload?.sub;
  return typeof uid === "string" && uid ? uid : undefined;
};
